import * as React from 'react';
import type { DisplayLayoutModeType } from '@sdk/schedule/types/DisplayLayoutModeType';
import type { LaborUISchemaType } from '@sdk/labor/types';

import type { RowType } from '../types/RowType';
import type {
  OpenEventCallback,
  AddEventType,
  IsShiftLineVisibleType,
  ShouldShowPlusType,
  IsSpecialDayType,
  OptionsFilterType,
} from '../types';
import type { ToggleEventType, IsEventSelectedType } from '../hooks/useEventSelection';
import AddEmployeeRow from '../components/AddEmployeeRow';
import LastRowSpacer from '../components/LastRowSpacer';

import HeaderTextRow from './HeaderTextRow';
import UnassignedSummaryRow from './UnassignedSummaryRow';
import SpecialGroupFirstRow from './SpecialGroupFirstRow';
import SpecialGroupRegularRow from './SpecialGroupRegularRow';
import RegularFirstRow from './RegularFirstRow';
import RegularRow from './RegularRow';
import SummaryRow from './SummaryRow';
import { COLUMNS_COUNT_WITH_NAME } from './constants';

const rowComponentDict = {
  SpecialGroupFirstRow,
  SpecialGroupRegularRow,
  RegularRow,
};

type Props = {
  row: RowType;
  currentPeriodDays: Array<string>;
  openEvent: OpenEventCallback;
  addUser: () => void;
  shiftAppearanceMode: DisplayLayoutModeType;
  addEvent: AddEventType;
  toggleEvent: ToggleEventType;
  hasSelectedEvents: boolean;
  isEventSelected: IsEventSelectedType;
  isShiftLineVisible: IsShiftLineVisibleType;
  shouldShowPlus: ShouldShowPlusType;
  laborUISchema: LaborUISchemaType;
  isBusinessClosed: IsSpecialDayType;
  isHolidayPay: IsSpecialDayType;
  optionsFilter: OptionsFilterType;
};
const RowRenderer: React.FC<React.PropsWithChildren<Props>> = ({
  row,
  currentPeriodDays,
  openEvent,
  addUser,
  shiftAppearanceMode,
  addEvent,
  toggleEvent,
  hasSelectedEvents,
  isEventSelected,
  isShiftLineVisible,
  shouldShowPlus,
  laborUISchema,
  isBusinessClosed,
  isHolidayPay,
  optionsFilter,
}) => {
  const { rowType, rowData } = row;

  let columnsCount = COLUMNS_COUNT_WITH_NAME;
  if ('hasNameColumn' in rowData && !rowData.hasNameColumn) {
    columnsCount = columnsCount - 1;
  }

  if (rowType === 'HeaderTextRow') {
    const { type, text, color } = rowData;
    return <HeaderTextRow type={type} text={text} color={color} />;
  }

  if (rowComponentDict[rowType]) {
    const RowComponent = rowComponentDict[rowType];
    return (
      <RowComponent
        columns={currentPeriodDays}
        rowData={rowData}
        openEvent={openEvent}
        columnsCount={columnsCount}
        shiftAppearanceMode={shiftAppearanceMode}
        addEvent={addEvent}
        toggleEvent={toggleEvent}
        hasSelectedEvents={hasSelectedEvents}
        isEventSelected={isEventSelected}
        isShiftLineVisible={isShiftLineVisible}
        shouldShowPlus={shouldShowPlus}
        isBusinessClosed={isBusinessClosed}
        isHolidayPay={isHolidayPay}
      />
    );
  }

  if (rowType === 'UnassignedSummaryRow' && optionsFilter.showCountOfHours) {
    return <UnassignedSummaryRow columns={currentPeriodDays} rowData={rowData} />;
  }

  if (rowType === 'RegularFirstRow') {
    return (
      <RegularFirstRow
        columns={currentPeriodDays}
        rowData={rowData}
        openEvent={openEvent}
        columnsCount={columnsCount}
        shiftAppearanceMode={shiftAppearanceMode}
        addEvent={addEvent}
        toggleEvent={toggleEvent}
        hasSelectedEvents={hasSelectedEvents}
        isEventSelected={isEventSelected}
        isShiftLineVisible={isShiftLineVisible}
        shouldShowPlus={shouldShowPlus}
        isBusinessClosed={isBusinessClosed}
        isHolidayPay={isHolidayPay}
        laborUISchema={laborUISchema}
        optionsFilter={optionsFilter}
      />
    );
  }

  if (rowType === 'SummaryRow') {
    return (
      <SummaryRow
        columns={currentPeriodDays}
        rowData={rowData}
        laborUISchema={laborUISchema}
        optionsFilter={optionsFilter}
      />
    );
  }

  if (rowType === 'AddUserRow') {
    return <AddEmployeeRow addUser={addUser} />;
  }

  if (rowType === 'LastRowSpacer') {
    return <LastRowSpacer />;
  }

  return null;
};

export default React.memo(RowRenderer);
